// Мутаторы партии: необязательные изменения правил на одну партию (сид дня, своя партия). В отличие от режима
// ребаланса мутатор ничего не подменяет в общей базе карт: движок читает G.mutators на месте (handSizeOf и т.п.),
// поэтому партии с разными мутаторами безопасно живут в одном процессе (арена, сервер). Набор мутаторов партии
// попадает в запись (GameRecord.mutators) и в реплей (ReplaySpec.mutators) — запись воспроизводится теми же.
// Новый мутатор: запись в MUTATORS + чтение флага в движке + RULES_VERSION + 1 (поле v — с какой версии он есть).
// Партии с мутаторами в таблицы «против ботов» не идут (несравнимы с обычными) — только в зал славы сида дня.
import type { Game, MutatorId } from '../types.ts';
import { RULES } from './rules.ts';

export interface MutatorDef {
  id: MutatorId;
  emoji: string;
  name: string;
  /** одна строка для плитки и ленты */
  short: string;
  /** полное описание для справки */
  desc: string;
  /** версия правил, с которой мутатор существует */
  v: number;
}

export const MUTATORS: Record<MutatorId, MutatorDef> = {
  fatHand: {
    id: 'fatHand',
    emoji: '🍔',
    name: 'Жирная рука',
    short: 'рука 6 карт вместо 5',
    desc: 'Каждый колдун берёт в руку 6 карт вместо 5 — в начале партии и в конце каждого хода. Добавки к пределу руки (жетон свойства, постоянки) считаются от шести. Партия короче и щедрее: мощи больше, колоды крутятся быстрее.',
    v: 22,
  },
  fragile: {
    id: 'fragile',
    emoji: '🥚',
    name: 'Хрупкие колдуны',
    short: '15 жизней вместо 20',
    desc: 'Колдуны начинают партию и воскресают с 15 жизнями вместо 20 (свои жизни жетона свойства и лошары — как обычно). Смертей больше, жетонов дохлых колдунов больше, беспределы страшнее.',
    v: 24,
  },
  richMarket: {
    id: 'richMarket',
    emoji: '🛍️',
    name: 'Богатая барахолка',
    short: 'барахолка 6 карт, легенд 4',
    desc: 'На барахолке 6 карт вместо 5, на легендарной — 4 вместо 3. Выбора больше, колоды уходят быстрее — партия чуть короче.',
    v: 24,
  },
  heritage: {
    id: 'heritage',
    emoji: '📦',
    name: 'Наследство',
    short: 'в стартовой колоде — случайная карта',
    desc: 'Каждый колдун начинает с одиннадцатой картой в колоде: верхней картой основной колоды не дороже 6 (беспределы пропускаются). У каждого свой первый козырь — и своя стратегия с первого хода.',
    v: 24,
  },
  catchUp: {
    id: 'catchUp',
    emoji: '🪜',
    name: 'Догоняющий',
    short: 'последний по очкам берёт карту',
    desc: 'В начале хода колдун, который отстаёт по очкам от всех остальных, берёт 1 карту. Отстающим легче вернуться в гонку, а лидеру — сложнее уйти в отрыв.',
    v: 24,
  },
  bespredelRound: {
    id: 'bespredelRound',
    emoji: '🌪️',
    name: 'Беспредельный круг',
    short: 'беспредел каждый круг',
    desc: 'В начале каждого круга (со второго) из основной колоды вскрывается ближайший к верху беспредел и тут же разыгрывается, как будто вышел на барахолку. Хаос гарантирован.',
    v: 24,
  },
  duel: {
    id: 'duel',
    emoji: '⚔️',
    name: 'Дуэль',
    short: 'один на один: отстающий берёт карту',
    desc: 'Правила дуэли один на один: в начале хода колдун, отстающий по очкам, берёт 1 карту, а отстающий на 10 ПО и больше — 2. Без этого вдвоём первая кровь часто решает партию — здесь отыграться можно до конца.',
    v: 24,
  },
};

export const MUTATOR_IDS: readonly MutatorId[] = Object.keys(MUTATORS) as MutatorId[];

export const isMutatorId = (x: unknown): x is MutatorId => typeof x === 'string' && x in MUTATORS;

/** Нормализованный набор: только известные id, без повторов, в порядке реестра (ключ записи стабилен) */
export function normalizeMutators(list: readonly unknown[] | undefined): MutatorId[] {
  if (!list) return [];
  const set = new Set(list.filter(isMutatorId));
  return MUTATOR_IDS.filter((id) => set.has(id));
}

export const hasMutator = (G: Pick<Game, 'mutators'>, id: MutatorId): boolean => !!G.mutators?.includes(id);

/** Мутаторы партии определениями (для плашек, справки, ленты) */
export const mutatorDefs = (G: Pick<Game, 'mutators'>): MutatorDef[] => (G.mutators ?? []).map((id) => MUTATORS[id]);

/** Базовый размер руки партии: 5 по правилам, 6 с «Жирной рукой»; добавки пассивов (handLimit) идут поверх */
export const handSizeOf = (G: Pick<Game, 'mutators'>): number => RULES.handSize + (hasMutator(G, 'fatHand') ? 1 : 0);

/** Жизни в начале и после смерти: 20 по правилам, 15 у «Хрупких колдунов» (свойства и лошара — поверх) */
export const startLifeOf = (G: Pick<Game, 'mutators'>): number => (hasMutator(G, 'fragile') ? 15 : RULES.startLife);

/** Размеры барахолок: 5 и 3 по правилам, 6 и 4 у «Богатой барахолки» */
export const marketSizeOf = (G: Pick<Game, 'mutators'>): number => RULES.marketSize + (hasMutator(G, 'richMarket') ? 1 : 0);
export const legendMarketSizeOf = (G: Pick<Game, 'mutators'>): number => RULES.legendMarketSize + (hasMutator(G, 'richMarket') ? 1 : 0);

/** Отстающий берёт карту в начале хода: «Догоняющий» и правила дуэли */
export const catchUpOn = (G: Pick<Game, 'mutators'>): boolean => hasMutator(G, 'catchUp') || hasMutator(G, 'duel');

/** Правила дуэли (подобраны лабораторией tools/mutator-lab.ts, см. docs/mutators.md; DUEL_TUNE в окружении — только для
 *  замеров): bigGap — с отставания на столько ПО отстающий берёт 2 карты вместо одной */
export const DUEL_TUNE: { bigGap: number } = { bigGap: 10 };
if (typeof process !== 'undefined' && process.env?.['DUEL_TUNE']) Object.assign(DUEL_TUNE, JSON.parse(process.env['DUEL_TUNE']) as object);

/** Мутаторы, которые игрок выбирает сам (дуэль включается столом: ты и один босс) */
export const PICKABLE_MUTATORS: readonly MutatorId[] = MUTATOR_IDS.filter((id) => id !== 'duel');

/** Подпись набора: «🍔 Жирная рука», несколько — через « · »; без мутаторов — «Классика» */
export function mutatorsLabel(list: readonly MutatorId[] | undefined, none = 'Классика'): string {
  if (!list?.length) return none;
  return list.map((id) => `${MUTATORS[id].emoji} ${MUTATORS[id].name}`).join(' · ');
}
